import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
    TextField,
    Button,
    MenuItem,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import 'moment/locale/tr'; // Türkçe dil desteği için
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

// Stil tanımlamaları
const chipStyles = {
    blue: { backgroundColor: '#F0F9FF', border: '1px solid #B9E6FE', color: '#026AA2', fontWeight: 500 },
    green: { backgroundColor: '#ECFDF3', border: '1px solid #ABEFC6', color: '#067647', fontWeight: 500 },
    red: { backgroundColor: '#FEF3F2', border: '1px solid #FECDCA', color: '#B42318', fontWeight: 500 },
    grey: { backgroundColor: '#F9FAFB', border: '1px solid #EAECF0', color: '#6B7280', fontWeight: 500 }
};

// Süre hesaplama yardımcısı fonksiyon
const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    const start = moment(startDate);
    const end = moment(endDate);
    const duration = moment.duration(end.diff(start));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours} Saat ${minutes} Dakika`;
};

export default function BoxOperationsPage() {
    // --- State'ler ---
    const [loading, setLoading] = useState(false); // Başlangıçta false
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    const [transactions, setTransactions] = useState([]); // Başlangıçta boş array
    const [locations, setLocations] = useState([]); // Dinamik lokasyonlar için state

    // Filtre state'leri
    const [searchText, setSearchText] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');

    const [startDate, setStartDate] = useState(moment().subtract(1, 'month'));
    const [endDate, setEndDate] = useState(moment());

    const [locationSearchText, setLocationSearchText] = useState('');
    const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isSubmittingSms, setIsSubmittingSms] = useState(false);

    const [isStartDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [isEndDatePickerOpen, setEndDatePickerOpen] = useState(false);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // --- YENİ useEffect: Sadece lokasyonları yükler ---
    useEffect(() => {
        // Sadece lokasyonları yüklemek için
        const fetchLocations = async () => {
            setLoading(true); // Lokasyonlar için loader'ı başlat
            setError(null);
            setTransactions([]); // Transaction'ları temizle (önemli)

            try {
                const locationsResponse = await Services.GetDropboxLockerLocations();

                const locationData = locationsResponse.data.result;
                // Gelen verinin array olduğunu garanti altına al
                setLocations(Array.isArray(locationData) ? locationData : []);

            } catch (err) {
                console.error("Failed to fetch locations:", err);
                setError('Lokasyonlar yüklenirken bir hata oluştu.');
                toast.error('Lokasyonlar yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false); // Lokasyonlar yüklendiğinde loader'ı kapat
            }
        };

        fetchLocations();
    }, []); // Bu useEffect sadece component ilk yüklendiğinde çalışır

    // useMemo, transactions her zaman array olacağı için güvende
    const filteredTransactions = useMemo(() => {
        // .filter() hatasını önlemek için transactions'ın array olduğundan emin ol
        if (!Array.isArray(transactions)) return [];

        return transactions.filter(op =>
            (op.nameSurName && op.nameSurName.toLowerCase().includes(searchText.toLowerCase())) ||
            (op.gsmNumber && op.gsmNumber.includes(searchText))
        );
    }, [transactions, searchText]);

    const pageCount = Math.ceil(filteredTransactions.length / itemsPerPage);
    const currentTableData = filteredTransactions.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const handleFilter = async () => {
        if (!selectedLocation) {
            toast.warn("Lütfen bir konum seçiniz.");
            return;
        }

        if (!startDate || !endDate) {
            toast.warn("Lütfen tarih aralığı seçiniz.");
            return;
        }

        setLoading(true);
        setError(null);
        setPage(1);
        try {
            const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
            const formattedEndDate = moment(endDate).add(1, 'days').format('YYYY-MM-DD');

            const response = await Services.GetDropboxAllTransactions(selectedLocation, formattedStartDate, formattedEndDate);
            const resultData = response.data.result;
            const transactionsArray = resultData && Array.isArray(resultData.transactions) ? resultData.transactions : [];

            setTransactions(transactionsArray);

            if (transactionsArray.length > 0) {
                toast.success("İşlemler başarıyla filtrelendi.");
            } else {
                toast.info("Seçili kriterlere uygun işlem bulunamadı.");
            }

        } catch (err) {
            console.error("Failed to filter transactions:", err);
            setError("İşlemler filtrelenirken bir hata oluştu.");
            toast.error("İşlemler filtrelenirken bir hata oluştu.");
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleGsmClick = (transaction) => {
        if (transaction.gsmNumber && transaction.otpCode) {
            setSelectedTransaction(transaction);
            setIsSmsModalOpen(true);
        } else {
            toast.info("Bu işlem için gönderilecek bir şifre veya telefon numarası bulunmuyor.");
        }
    };

    const handleSendSms = async () => {
        if (!selectedTransaction) return;
        const isConfirmed = window.confirm(
            `'${selectedTransaction.nameSurName}' adlı kişiye ait ${selectedTransaction.gsmNumber} numarasına mevcut dolap şifresini göndermek istediğinizden emin misiniz?`
        );

        if (isConfirmed) {
            setIsSubmittingSms(true);
            try {
                const message = `EasyPoint dolap şifreniz: ${selectedTransaction.lockPass}. Lütfen kimseyle paylaşmayınız.`;
                await Services.BoxOperationSendingSMS(selectedTransaction.gsmNumber, message);
                toast.success("Şifre başarıyla SMS olarak gönderildi.");
            } catch (error) {
                console.error("SMS gönderme hatası:", error);
                toast.error("SMS gönderilirken bir hata oluştu.");
            } finally {
                setIsSubmittingSms(false);
                setIsSmsModalOpen(false);
                setSelectedTransaction(null);
            }
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="tr">
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold">Dolap İşlemleri</Typography>
                </Stack>
                <Card>
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Dolap İşlemleri Listesi</Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                            <TextField size="small" placeholder="Kişi veya GSM No ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>), }} />
                            <TextField
                                select
                                label="Konum"
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                size="small"
                                sx={{ minWidth: 240 }}
                            >
                                <Box onKeyDown={e => e.stopPropagation()} sx={{ p: 1, pt: 0, borderBottom: '1px solid #eee' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Konum adı arayın..."
                                        value={locationSearchText}
                                        onChange={(e) => setLocationSearchText(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>)
                                        }}
                                    />
                                </Box>
                                <MenuItem value="">Tüm Lokasyonlar</MenuItem>
                                {locations
                                    .filter(loc => loc.name.toLowerCase().includes(locationSearchText.toLowerCase()))
                                    .map((loc) => (<MenuItem key={loc.locationCode} value={loc.locationCode}>{`(${loc.locationCode}) ${loc.name}`}</MenuItem>))
                                }
                            </TextField>
                            <TextField
                                ref={startDateRef}
                                label="Başlangıç Tarihi"
                                size="small"
                                value={startDate ? moment(startDate).format('DD.MM.YYYY') : ''}
                                onClick={() => setStartDatePickerOpen(true)}
                                InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><CalendarTodayIcon color="action" /></InputAdornment>), }}
                            />
                            <TextField
                                ref={endDateRef}
                                label="Bitiş Tarihi"
                                size="small"
                                value={endDate ? moment(endDate).format('DD.MM.YYYY') : ''}
                                onClick={() => setEndDatePickerOpen(true)}
                                InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><CalendarTodayIcon color="action" /></InputAdornment>), }}
                            />
                            <Button onClick={handleFilter} variant="contained" sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' }, px: 3 }}>
                                Filtrele
                            </Button>
                        </Stack>
                    </Box>

                    {/* Tarih seçiciler */}
                    <DatePicker open={isStartDatePickerOpen} onClose={() => setStartDatePickerOpen(false)} value={startDate} onChange={(newValue) => { setStartDate(newValue); setStartDatePickerOpen(false); }} slotProps={{ popper: { anchorEl: startDateRef.current }, textField: { sx: { display: 'none' } } }} />
                    <DatePicker open={isEndDatePickerOpen} onClose={() => setEndDatePickerOpen(false)} value={endDate} onChange={(newValue) => { setEndDate(newValue); setEndDatePickerOpen(false); }} slotProps={{ popper: { anchorEl: endDateRef.current }, textField: { sx: { display: 'none' } } }} />

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                    ) : (
                        <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mx: 2, maxWidth: 'calc(100% - 32px)' }}>
                            <Table sx={{ minWidth: 1640 }} aria-label="dolap işlemleri tablosu">
                                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                                    <TableRow>
                                        <TableCell>İŞLEM NO</TableCell>
                                        <TableCell>KONUM</TableCell>
                                        <TableCell>DOLAP NO</TableCell>
                                        <TableCell>BAŞLANGIÇ TARİHİ</TableCell>
                                        <TableCell>BİTİŞ TARİHİ</TableCell>
                                        <TableCell>ADI SOYADI</TableCell>
                                        <TableCell>GSM / E-MAIL</TableCell>
                                        <TableCell>SİPARİŞ TÜRÜ</TableCell>
                                        <TableCell>SÜRE</TableCell>
                                        <TableCell>ÖDEME TÜRÜ</TableCell>
                                        <TableCell>FİYAT</TableCell>
                                        <TableCell>DOLAP ŞİFRESİ</TableCell>
                                        <TableCell>DOĞRULAMA KODU</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* --- GÜNCELLENDİ: Tablo body'si API verileriyle ve yeni alan adlarıyla dolduruldu --- */}
                                    {currentTableData.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            {/* İşlem No */}
                                            <TableCell>{row.id || '-'}</TableCell>
                                            {/* Konum */}
                                            <TableCell>{row.locationCode || '-'}</TableCell>
                                            {/* Dolap No */}
                                            <TableCell><Chip label={row.boxNumber || row.lockerNumber || '-'} size="small" sx={chipStyles.grey} /></TableCell>
                                            {/* Başlangıç Tarihi */}
                                            <TableCell>{row.startDate ? moment(row.startDate).format('DD.MM.YYYY HH:mm') : '-'}</TableCell>
                                            {/* Bitiş Tarihi */}
                                            <TableCell>{row.endDate ? moment(row.endDate).format('DD.MM.YYYY HH:mm') : '-'}</TableCell>
                                            {/* Adı Soyadı */}
                                            <TableCell>{row.nameSurName || '-'}</TableCell>
                                            {/* GSM / E-MAIL */}
                                            <TableCell>
                                                <Chip
                                                    label={row.gsmNumber || row.email || '-'}
                                                    size="small"
                                                    sx={{
                                                        ...chipStyles.blue,
                                                        // lockPass null olsa bile SMS gönderilebiliyor mu kontrol edilmeli. 
                                                        // API yanıtında lockPass null geliyor. Belki başka bir alandan alınmalı?
                                                        // Şimdilik sadece gsmNumber varsa tıklanabilir yapalım.
                                                        ...(row.gsmNumber && { cursor: 'pointer' })
                                                    }}
                                                    onClick={() => handleGsmClick(row)}
                                                />
                                            </TableCell>
                                            {/* Sipariş Türü (orderType: 1 veya 2?) - Mapping gerekebilir */}
                                            <TableCell>{row.orderType === 1 ? 'Emanet' : row.orderType === 2 ? 'Teslim Alma' : '-'}</TableCell>
                                            {/* Süre */}
                                            <TableCell>{calculateDuration(row.startDate, row.endDate)}</TableCell>
                                            {/* Ödeme Türü (paymentType: '1' -> Kredi Kartı, '3' -> Kupon ?) - Mapping gerekebilir */}
                                            <TableCell>{row.paymentType === '1' ? 'Kredi Kartı' : row.paymentType === '3' ? 'Kupon' : '-'}</TableCell>
                                            {/* Fiyat */}
                                            <TableCell>
                                                <Chip
                                                    label={row.price !== null ? `${row.price} TL` : '-'}
                                                    size="small"
                                                    sx={chipStyles.green}
                                                />
                                            </TableCell>
                                            {/* Dolap Şifresi (lockPass null geliyor, belki otpCode mu gösterilmeli?) */}
                                            <TableCell>
                                                <Chip
                                                    icon={<LockOutlinedIcon sx={{ color: `${chipStyles.red.color} !important`, fontSize: '16px' }} />}
                                                    // lockPass null ise otpCode'u gösterelim mi? Ya da sadece '****'?
                                                    label={row.lockPass || row.otpCode || '****'}
                                                    size="small"
                                                    sx={chipStyles.red}
                                                />
                                            </TableCell>
                                            {/* Doğrulama Kodu */}
                                            <TableCell><Chip label={row.verificationCode || '-'} size="small" sx={chipStyles.grey} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {`Toplam ${filteredTransactions.length} kayıttan ${filteredTransactions.length > 0 ? (page - 1) * itemsPerPage + 1 : 0}–${Math.min(page * itemsPerPage, filteredTransactions.length)} arası gösteriliyor.`}
                        </Typography>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </Stack>
                </Card>

                {/* SMS Gönderme Modalı */}
                <Dialog open={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} PaperProps={{ sx: { borderRadius: '12px', width: 450 } }}>
                    <DialogTitle fontWeight={600}>Mevcut Şifreyi Gönder</DialogTitle>
                    <DialogContent>
                        <Typography>
                            <b>{selectedTransaction?.nameSurName}</b> adlı kişiye ait
                            <b> {selectedTransaction?.gsmNumber}</b> telefon numarasına,
                            daha önce oluşturulmuş olan dolap şifresini tekrar göndermek üzeresiniz.
                        </Typography>
                        <Box sx={{ p: 2, mt: 2, backgroundColor: '#F9FAFB', borderRadius: '8px', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">DOLAP ŞİFRESİ</Typography>
                            <Typography variant="h5" fontWeight={700} letterSpacing={2}>
                                {selectedTransaction?.otpCode || '****'}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 1 }}>
                        <Button onClick={() => setIsSmsModalOpen(false)}>Vazgeç</Button>
                        <Button
                            onClick={handleSendSms}
                            variant="contained"
                            sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}
                            disabled={isSubmittingSms}
                        >
                            {isSubmittingSms ? <CircularProgress size={24} color="inherit" /> : 'Onayla ve Gönder'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
}